import React, { useState, useEffect, useRef } from 'react';
import { DatePicker, Button, Space, TimePicker } from 'antd';
import dayjs from 'dayjs';
import { DATE_PRESETS } from './datePresets';

const DateRangeSelector = ({ value, onChange, activePreset, onPresetChange }) => {
    // Track pending changes that haven't been applied yet
    const [pendingStartDate, setPendingStartDate] = useState(null);
    const [pendingEndDate, setPendingEndDate] = useState(null);
    const [pendingStartTime, setPendingStartTime] = useState(null);
    const [pendingEndTime, setPendingEndTime] = useState(null);

    // Initialize component with the active preset
    const initRef = useRef(false);

    useEffect(() => {
        if (!initRef.current && activePreset) {
            // Find the preset that matches the activePreset key
            const preset = DATE_PRESETS.find(p => p.key === activePreset);
            if (preset) {
                // Only set the values if they're not already set
                if (!value || !value.start || !value.end) {
                    const [start, end] = preset.range();
                    onChange({ start: start.toDate(), end: end.toDate() });
                }
            }
            initRef.current = true;
        }
    }, [activePreset, value, onChange]);

    // Determine if we have pending changes that need to be applied
    const hasPendingChanges = pendingStartDate !== null || pendingEndDate !== null ||
        pendingStartTime !== null || pendingEndTime !== null;

    // Handle preset button clicks
    const handlePresetClick = (preset) => {
        // Clear any pending changes
        setPendingStartDate(null);
        setPendingEndDate(null);
        setPendingStartTime(null);
        setPendingEndTime(null);

        const [start, end] = preset.range();
        onChange({ start: start.toDate(), end: end.toDate() });

        if (onPresetChange) {
            onPresetChange(preset.key);
        }
    };

    // Handle start date change
    const handleStartDateChange = (date) => {
        setPendingStartDate(date);
        if (onPresetChange && activePreset) {
            onPresetChange(null); // Clear preset when a custom date is pending
        }
    };

    // Handle end date change
    const handleEndDateChange = (date) => {
        setPendingEndDate(date);
        if (onPresetChange && activePreset) {
            onPresetChange(null); // Clear preset when a custom date is pending
        }
    };

    // Handle start time change
    const handleStartTimeChange = (time) => {
        setPendingStartTime(time);
        if (onPresetChange && activePreset) {
            onPresetChange(null); // Clear preset when a custom time is pending
        }
    };

    // Handle end time change
    const handleEndTimeChange = (time) => {
        setPendingEndTime(time);
        if (onPresetChange && activePreset) {
            onPresetChange(null); // Clear preset when a custom time is pending
        }
    };

    // Apply pending changes
    const applyChanges = () => {
        // Safety check - if value is null, provide default values
        const safeValue = value || {
            start: new Date(new Date().setDate(new Date().getDate() - 1)),
            end: new Date()
        };

        let newStart = safeValue.start;
        let newEnd = safeValue.end;

        // Apply date changes if any
        if (pendingStartDate) {
            const startDate = dayjs(pendingStartDate);

            // Combine date and time
            if (pendingStartTime) {
                newStart = startDate
                    .hour(pendingStartTime.hour())
                    .minute(pendingStartTime.minute())
                    .second(0)
                    .toDate();
            } else {
                // Use the date with current time
                const currentTime = dayjs(safeValue.start);
                newStart = startDate
                    .hour(currentTime.hour())
                    .minute(currentTime.minute())
                    .second(0)
                    .toDate();
            }
        } else if (pendingStartTime) {
            // Only time changed, apply to current date
            const currentDate = dayjs(safeValue.start);
            newStart = currentDate
                .hour(pendingStartTime.hour())
                .minute(pendingStartTime.minute())
                .second(0)
                .toDate();
        }

        // Apply end date changes if any
        if (pendingEndDate) {
            const endDate = dayjs(pendingEndDate);

            // Combine date and time
            if (pendingEndTime) {
                newEnd = endDate
                    .hour(pendingEndTime.hour())
                    .minute(pendingEndTime.minute())
                    .second(59)
                    .toDate();
            } else {
                // Use the date with current time
                const currentTime = dayjs(safeValue.end);
                newEnd = endDate
                    .hour(currentTime.hour())
                    .minute(currentTime.minute())
                    .second(59)
                    .toDate();
            }
        } else if (pendingEndTime) {
            // Only time changed, apply to current date
            const currentDate = dayjs(safeValue.end);
            newEnd = currentDate
                .hour(pendingEndTime.hour())
                .minute(pendingEndTime.minute())
                .second(59)
                .toDate();
        }

        // Update the actual date range
        onChange({ start: newStart, end: newEnd });

        // Clear pending changes
        setPendingStartDate(null);
        setPendingEndDate(null);
        setPendingStartTime(null);
        setPendingEndTime(null);
    };

    // Reset pending changes
    const cancelChanges = () => {
        setPendingStartDate(null);
        setPendingEndDate(null);
        setPendingStartTime(null);
        setPendingEndTime(null);
    };

    // Use a default value if value is null - but only for calculations, not display
    const safeValue = value || {
        start: new Date(new Date().setDate(new Date().getDate() - 1)),
        end: new Date()
    };

    // Display values (either pending or actual)
    const displayStartDate = pendingStartDate || (value && value.start ? dayjs(value.start) : null);
    const displayEndDate = pendingEndDate || (value && value.end ? dayjs(value.end) : null);
    const displayStartTime = pendingStartTime || (value && value.start ? dayjs(value.start) : null);
    const displayEndTime = pendingEndTime || (value && value.end ? dayjs(value.end) : null);

    return (
        <div className="date-range-controls">
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                {/* Preset buttons */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {DATE_PRESETS.map(preset => (
                        <Button
                            key={preset.key}
                            type={activePreset === preset.key ? 'primary' : 'default'}
                            onClick={() => handlePresetClick(preset)}
                            style={{
                                borderRadius: '20px',
                                backgroundColor: activePreset === preset.key ? '#10b981' : undefined,
                                borderColor: activePreset === preset.key ? '#10b981' : undefined
                            }}
                        >
                            {preset.label}
                        </Button>
                    ))}
                </div>

                {/* Date and time selection controls - matched to the original layout */}
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <label style={{
                            color: '#6b7280',
                            fontSize: '14px',
                            minWidth: '50px'
                        }}>
                            Start Date:
                        </label>
                        <DatePicker
                            value={displayStartDate}
                            onChange={handleStartDateChange}
                            allowClear={false}
                            format="YYYY-MM-DD"
                            style={{ width: '150px' }}
                        />
                        <TimePicker
                            value={displayStartTime}
                            onChange={handleStartTimeChange}
                            format="HH:mm"
                            minuteStep={5}
                            allowClear={false}
                            style={{ width: '90px' }}
                        />
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <label style={{
                            color: '#6b7280',
                            fontSize: '14px',
                            minWidth: '50px'
                        }}>
                            End Date:
                        </label>
                        <DatePicker
                            value={displayEndDate}
                            onChange={handleEndDateChange}
                            allowClear={false}
                            format="YYYY-MM-DD"
                            style={{ width: '150px' }}
                        />
                        <TimePicker
                            value={displayEndTime}
                            onChange={handleEndTimeChange}
                            format="HH:mm"
                            minuteStep={5}
                            allowClear={false}
                            style={{ width: '90px' }}
                        />
                    </div>

                    {hasPendingChanges && (
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <Button
                                type="link"
                                onClick={applyChanges}
                                style={{
                                    padding: '4px 8px',
                                    height: 'auto',
                                    color: '#1890ff'
                                }}
                            >
                                Apply
                            </Button>
                            <Button
                                type="link"
                                onClick={cancelChanges}
                                style={{
                                    padding: '4px 8px',
                                    height: 'auto',
                                    color: '#6b7280'
                                }}
                            >
                                Cancel
                            </Button>
                        </div>
                    )}
                </div>
            </Space>
        </div>
    );
};

export default DateRangeSelector;
